import {
  Box,
  Button,
  Divider,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  SvgIcon,
  Theme,
  Typography,
  alpha,
  useMediaQuery,
} from "@mui/material";
import viewpaths from "global/constants/ViewPathConstants";
import { useAppSelector } from "hooks/use-app-select";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  loginAction,
  selectAuthenticated,
  selectLastVisitedLink,
} from "redux/authSlice";
import history from "utils/history";
import {
  checkSamlRegistration,
  initiateSamlLogin,
  login,
  oAuthLogin,
} from "screens/Auth/AuthService";
import {
  establishSession,
  getAccountDisplayName,
  setSsoRequestedEmail,
} from "./authSession";
import { useAppDispatch } from "hooks/use-app-dispatch";
import {
  convertResourceToObjectFormat,
  handleErrorMessage,
  hasGoogleAuthProvider,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import urls from "global/constants/UrlConstants";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import strings from "global/constants/StringConstants";
import { CustomInput } from "global/components";
import { useAuthRouteRedirection } from "layouts/Config";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import defaultImage from "assets/icons1/avatars/default.png";
import { updatePreference } from "redux/preferencesSlice";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import {
  handleLoginValidation,
  loginFormField,
} from "./AuthTypesAndValidation";
import _ from "lodash";
import { getCredentialOptions } from "screens/Settings/SettingsService";
import Logo from "assets/images/side_nav_inkfree_logo.svg";
import { GoogleLogin } from "@react-oauth/google";
import { focusRing, inkfree, mobile, signer } from "theme/colors";
import {
  authRef,
  refFont,
  authPrimaryButtonSx,
  authSecondaryButtonSx,
  authSecondaryButtonWithIconSx,
  googleButtonSlot,
  googleOverlaySx,
  GOOGLE_BUTTON_WIDTH,
} from "./AuthReferenceStyles";
import { GoogleGlyph } from "./AuthGlyphs";

// Colors specific to this page's brand panel, from the Auth Screens reference.
const panelRef = {
  ...authRef,
  pendingTint: "#FDF3D7", // PENDING chip background
  pendingAvatarBg: "#F4F6FD", // not-yet-signed avatar fill
  pendingAvatarBorder: "#B9C2E0", // not-yet-signed avatar dashed border
  pendingAvatarText: signer.text.faint, // not-yet-signed avatar initials (WCAG 1.4.3, was #8B87A0 at 3.2:1)
};

export {
  buildBaseLoginPayload,
  shouldShowPasswordExpiryPage,
  buildExpiryPayload,
  handlePasswordExpiryRedirect,
} from "./authSession";

const focusRingSx = {
  "&:focus-visible": {
    outline: `2px solid ${focusRing}`,
    outlineOffset: "2px",
  },
};

const Login = () => {
  useTitle(title.LOGIN);
  const dispatch = useAppDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const emailRegex = strings.regex;
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const [processing, setProcessing] = useState<boolean>(false);
  const [credentials, setCredentials] = useState(loginFormField);
  const [visibility, setVisibility] = useState(false);
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const [isPasskeyAvailable, setIsPasskeyAvailable] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState<boolean>(false);
  const current = useAppSelector(selectLastVisitedLink);
  const [showSso, setShowSso] = useState<boolean>(false);
  const [ssoEmail, setSsoEmail] = useState<string>("");
  const [ssoError, setSsoError] = useState<string>("");
  const location = useLocation();
  const handledSsoFailure = useRef(false);
  // Explicit override for callers that need a specific return path (the MCP
  // auth bridge) rather than the generic last-visited-link tracking `current`
  // covers. Takes priority over `current` when present.
  const redirectionParam = new URLSearchParams(location.search).get(
    "redirectionUrl",
  );
  const decodedUrl = redirectionParam
    ? decodeURIComponent(redirectionParam)
    : undefined;
  const returnTo = decodedUrl ?? current;

  useEffect(() => {
    if (handledSsoFailure.current) return;
    handledSsoFailure.current = true;

    const params = new URLSearchParams(location.search);
    const reason = params.get("reason");
    const message = params.get("message");
    const error = params.get("error");

    if (!reason && !message && !error) return;

    if (error || reason) {
      openErrorNotification(
        message ?? "Single sign-on failed. Please try signing in again.",
      );
    } else if (message) {
      openSuccessNotification(message);
    }
    history.replace(viewpaths.loginViewPath);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSsoContinue = async () => {
    const email = ssoEmail.trim();
    if (!email) {
      setSsoError("Please enter your work email");
      return;
    }
    if (!emailRegex.test(email)) {
      setSsoError("Please enter a valid email");
      return;
    }
    setSsoError("");
    setProcessing(true);
    try {
      const { ssoConfigured } = await checkSamlRegistration(email);
      if (!ssoConfigured) {
        setSsoError(
          "No single sign-on is set up for this email. Contact your admin or sign in with a password.",
        );
        setProcessing(false);
        return;
      }
    } catch {
      setSsoError("Couldn't verify single sign-on right now. Please try again.");
      setProcessing(false);
      return;
    }
    setSsoRequestedEmail(email);
    initiateSamlLogin(email);
  };

  const checkPasskeyAvailability = async () => {
    if (window.PublicKeyCredential) {
      const isUserVerifyingAvailable =
        await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      if (isUserVerifyingAvailable) {
        setIsPasskeyAvailable(true);
      }
    }
  };

  useEffect(() => {
    checkPasskeyAvailability();
  }, []);

  const handleChange = (event: any) => {
    setCredentials({
      ...credentials,
      [event.target.name]: {
        value: event.target.value,
        error: "",
      },
    });
  };

  const doLogin = async () => {
    try {
      if (handleLoginValidation(credentials).isValid) {
        setProcessing(true);
        let captchaToken: any;
        if (urls.PROD) {
          captchaToken = await executeRecaptcha!();
        }
        const user = await login(
          credentials.email.value,
          credentials.password.value,
          captchaToken,
        );
        establishSession(user, dispatch, returnTo, credentials.password.value);
        setProcessing(false);
      }
    } catch (error: any) {
      const errorCode =
        error?.errorCode ??
        error?.error?.errorCode ??
        error?.code ??
        error?.error?.code;
      const isSsoEnforced =
        errorCode === strings.ssoEnforcedErrorCode ||
        // Keep compatibility with older API responses until they include the
        // stable SSO_ENFORCED error code.
        error?.message === strings.ssoEnforcedErrorMessage;

      if (isSsoEnforced) {
        setSsoEmail(credentials.email.value);
        setShowSso(true);
        setSsoError("Your organization requires single sign-on. Continue below.");
        return;
      }
      if (error == null) {
        openErrorNotification("Unable to sign in. Please try again.");
      } else {
        handleErrorMessage(error);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleOauthLogin = async (token: string, provider: string) => {
    try {
      setProcessing(true);
      const res = await oAuthLogin(token, provider);
      setProcessing(false);
      return res;
    } catch (error: any) {
      setProcessing(false);
      handleErrorMessage(error);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse: any) => {
    const token = credentialResponse.credential;
    let user: any;
    if (!token) return;
    try {
      user = await handleOauthLogin(token, strings.OAuthProviders.googleAuth);

      if (user?.authToken) {
        const formattedResources = convertResourceToObjectFormat(
          user.resources || {},
        );

        dispatch(
          loginAction({
            authenticated: true,
            accessToken: user.authToken,
            refreshToken: user.refreshToken,
            userName: user.name,
            email: (user.email ?? "").toLowerCase(),
            userRole: user.role,
            resources: formattedResources,
            profileImage: user.profilePicture
              ? "data:image/png;base64," + user.profilePicture
              : defaultImage,
            deviceId: "",
            account: user.account,
            accountDisplayName: getAccountDisplayName(user),
            userId: user.id,
            isTrial: user.trial,
            lastLogin: user.lastLogin,
          }),
        );
        history.push(decodedUrl ?? viewpaths.dashboardViewPath);
      } else {
        const hasGoogleProvider = hasGoogleAuthProvider(
          user.authProviders || [],
        );

        if (hasGoogleProvider) {
          const googleUserData = {
            email: user.email,
            name: user.name || "",
            authProviders: user.authProviders || [],
          };

          history.push({
            pathname: viewpaths.oAuthRegisterViewPath,
            state: googleUserData,
          });
        }
      }
    } catch (error: any) {
      if (error.message === strings.pendingVerificationErrorMessage) {
        history.push({
          pathname: viewpaths.verificationPendingViewPath,
          state: {
            email: user?.email,
            isFromLoginScreen: true,
          },
        });
      } else {
        handleErrorMessage(error);
      }
    }
  };

  const handleLoginUsingPasskey = async () => {
    let timeout: NodeJS.Timeout | null = null;
    try {
      setPasskeyLoading(true);
      timeout = setTimeout(() => {
        setPasskeyLoading(false);
        openErrorNotification("Passkey operation timed out.");
      }, 30000);
      let userType: any;
      const user = await getCredentialOptions();
      if (timeout) clearTimeout(timeout);

      if (user) {
        const formattedResources = convertResourceToObjectFormat(
          user.resources,
        );

        if (isTruthy(formattedResources)) {
          dispatch(
            loginAction({
              authenticated: true,
              accessToken: user.authToken,
              refreshToken: user.refreshToken,
              userName: user.name,
              email: (user.email ?? "").toLowerCase(),
              userRole: user.role,
              resources: formattedResources,
              profileImage: user.profilePicture
                ? "data:image/png;base64," + user.profilePicture
                : defaultImage,
              type: userType?.message ?? "",
              deviceId: "",
              account: user.account,
              accountDisplayName: getAccountDisplayName(user),
              userId: user.id,
              isTrial: user.trial,
            }),
          );
          dispatch(
            updatePreference({
              layout: "vertical",
              theme: "dark",
            }),
          );
        } else {
          openErrorNotification("Something went wrong.");
        }
        history.push(viewpaths.dashboardViewPath);
      }
    } catch (error: any) {
      if (timeout) clearTimeout(timeout);
      handleErrorMessage(error);
    } finally {
      if (timeout) clearTimeout(timeout);
      setPasskeyLoading(false);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      doLogin();
    }
  };

  const registerPage = () => {
    history.push(viewpaths.registerViewPath);
  };

  if (isLoggedIn) {
    history.push(decodedUrl ?? authRoute);
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        width: "100%",
        overflowX: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          flex: { xs: "1 1 100%", md: "0 0 50%" },
          minWidth: 0,
          maxWidth: { xs: "100%", md: "50%" },
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          boxSizing: "border-box",
          outline: "none",
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7, lg: 9 },
            pt: { xs: 4, sm: 5, md: 5.5 },
            pb: { xs: 4, sm: 4 },
            maxWidth: { xs: "100%", sm: 600, md: 560 },
            width: "100%",
            mx: "auto",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <img
              src={Logo}
              alt="Inkfree logo"
              style={{ width: 112, height: "auto", display: "block" }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              mt: { xs: 4, md: 0 },
            }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: "1.75rem", sm: refFont.heading },
                fontWeight: 800,
                color: mobile.base[500],
                mb: 1.25,
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              Log in
            </Typography>

            <Typography
              sx={{
                mb: 4.25,
                fontSize: refFont.body,
                color: panelRef.subText,
              }}
            >
              New around here?{" "}
              <Link
                component="span"
                onClick={registerPage}
                role="button"
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    registerPage();
                  }
                }}
                sx={{
                  cursor: "pointer",
                  color: signer.orange.main,
                  fontSize: "inherit",
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                  ...focusRingSx,
                }}
              >
                Create an account
              </Link>
            </Typography>

            <Box
              sx={{
                // Field chrome per reference: 48px tall, 8px radius, pale fill,
                // 14px inset. `&&` outranks CustomInput's own !important rules.
                "&& .MuiOutlinedInput-root": {
                  minHeight: "48px",
                  height: "48px",
                  borderRadius: "8px",
                  padding: "0 14px !important",
                  backgroundColor: panelRef.inputBg,
                  fontSize: refFont.body,
                  fontWeight: 500,
                  color: mobile.base[500],
                  "& fieldset": { borderColor: panelRef.inputBorder },
                  "&:hover fieldset": { borderColor: panelRef.inputBorder },
                },
                "&& .MuiInputBase-input": {
                  padding: "0 !important",
                },
                "&& .MuiInputBase-input::placeholder": {
                  color: panelRef.metaText,
                  opacity: 1,
                },
                "&& .MuiInputLabel-root": {
                  fontSize: refFont.label,
                  fontWeight: 600,
                  color: mobile.base[500],
                  marginLeft: 0,
                  "& .MuiFormLabel-asterisk": { color: signer.orange.main },
                },
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <CustomInput
                    id="login-email"
                    required
                    label="Business email"
                    autoFocus
                    placeHolder="you@company.com"
                    name="email"
                    onChange={handleChange}
                    type="email"
                    value={credentials.email.value}
                    disabled={processing}
                    propsToInputElement={{
                      maxLength: 50,
                      "aria-required": true,
                    }}
                    error={
                      !isTruthy(credentials.email.value) &&
                      credentials.email.error
                    }
                    onKeyPress={handleKeypress}
                    helperText={
                      !isTruthy(credentials.email.value)
                        ? credentials.email.error
                        : ""
                    }
                  />
                  {!emailRegex.test(credentials.email.value) &&
                    credentials.email.value.length > 0 && (
                      <FormHelperText error role="alert" sx={{ paddingLeft: "14px" }}>
                        Please enter valid email
                      </FormHelperText>
                    )}
                </Box>

                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 2,
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      component="label"
                      htmlFor="login-password"
                      sx={{
                        fontWeight: 600,
                        color: mobile.base[500],
                        fontSize: refFont.label,
                        lineHeight: 1.4,
                      }}
                    >
                      Password{" "}
                      <Box
                        component="span"
                        sx={{ color: signer.orange.main }}
                      >
                        *
                      </Box>
                    </Typography>
                    <Link
                      underline="hover"
                      sx={{
                        color: signer.orange.main,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontSize: refFont.link,
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: signer.orange.main,
                          outlineOffset: "2px",
                        },
                      }}
                      onClick={() =>
                        history.push(viewpaths.forgotPasswordViewPath)
                      }
                      onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          history.push(viewpaths.forgotPasswordViewPath);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Forgot password? Reset your password"
                    >
                      Forgot password?
                    </Link>
                  </Box>
                  <CustomInput
                    id="login-password"
                    required
                    placeHolder="••••••••"
                    name="password"
                    onChange={handleChange}
                    type={visibility ? "text" : "password"}
                    value={credentials.password.value}
                    disabled={processing}
                    onKeyPress={handleKeypress}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            aria-label={
                              visibility ? "Hide password" : "Show password"
                            }
                            onClick={() => setVisibility(!visibility)}
                            edge="end"
                            tabIndex={0}
                          >
                            <SvgIcon>
                              {visibility ? (
                                <VisibilityOutlinedIcon />
                              ) : (
                                <VisibilityOffOutlinedIcon />
                              )}
                            </SvgIcon>
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    propsToInputElement={{
                      maxLength: 30,
                      "aria-required": true,
                    }}
                    error={
                      credentials.password.value.length < 8
                        ? credentials.password.error
                        : null
                    }
                    helperText={
                      credentials.password.error
                        ? credentials.password.error
                        : ""
                    }
                  />
                </Box>
              </Stack>
            </Box>

            <Button
              fullWidth
              size="large"
              sx={{
                mt: 3.25,
                height: "50px",
                borderRadius: "8px",
                fontWeight: 700,
                fontSize: refFont.button,
                letterSpacing: 0,
              }}
              type="submit"
              variant="contained"
              onClick={doLogin}
              disabled={
                _.isEmpty(credentials.email.value) ||
                _.isEmpty(credentials.password.value) ||
                !emailRegex.test(credentials.email.value) ||
                credentials.password?.value?.length < 8 ||
                processing
              }
              aria-label={
                processing
                  ? "Logging in, please wait"
                  : "Log in to your account"
              }
            >
              {processing ? "Logging in..." : "Log in"}
            </Button>

            <Divider
              sx={{
                my: 3.25,
                "&:before, &:after": { borderColor: panelRef.dividerLine },
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: panelRef.metaText,
                  px: 1.5,
                  fontWeight: 600,
                  fontSize: refFont.divider,
                  letterSpacing: "0.08em",
                }}
              >
                OR CONTINUE WITH
              </Typography>
            </Divider>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  ...googleButtonSlot,
                  // The row stacks to a column at xs, where `flex: 1 1 0` would
                  // put the 0 basis on the vertical axis and collapse the slot.
                  flex: { xs: "0 0 auto", sm: "1 1 0" },
                  width: { xs: "100%", sm: "auto" },
                  minWidth: 0,
                }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={processing}
                  tabIndex={-1}
                  aria-hidden
                  startIcon={<GoogleGlyph />}
                  sx={authSecondaryButtonWithIconSx}
                >
                  Google
                </Button>
                <Box sx={googleOverlaySx(processing)}>
                  <GoogleLogin
                    theme="outline"
                    size="large"
                    width={GOOGLE_BUTTON_WIDTH}
                    text="signin_with"
                    onSuccess={handleGoogleLoginSuccess}
                  />
                </Box>
              </Box>

              {isPasskeyAvailable && (
                <Button
                  fullWidth
                  size="large"
                  type="submit"
                  variant="outlined"
                  onClick={handleLoginUsingPasskey}
                  disabled={passkeyLoading || processing}
                  startIcon={
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M2 20c0-3.314 2.686-6 6-6"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                      <path
                        d="M15 13l2 2 4-4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <rect
                        x="13"
                        y="11"
                        width="8"
                        height="6"
                        rx="1.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                    </svg>
                  }
                  sx={{
                    ...authSecondaryButtonSx,
                    flex: { xs: "0 0 auto", sm: "1 1 0" },
                    width: { xs: "100%", sm: "auto" },
                    minWidth: 0,
                  }}
                  aria-label={
                    passkeyLoading
                      ? "Logging in with passkey, please wait"
                      : "Login using passkey authentication"
                  }
                >
                  {passkeyLoading ? "Logging in..." : "Passkey"}
                </Button>
              )}
            </Stack>

            <Box sx={{ width: "100%", mt: 1.5 }}>
              {!showSso ? (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setShowSso(true)}
                  disabled={processing}
                  startIcon={<BusinessOutlinedIcon sx={{ fontSize: 18 }} />}
                  // Starts with the visible text so voice control users can say
                  // what they see (WCAG 2.5.3).
                  aria-label="Sign in with SSO (enterprise single sign-on)"
                  sx={authSecondaryButtonWithIconSx}
                >
                  Sign in with SSO
                </Button>
              ) : (
                <Stack spacing={1.5} sx={{ width: "100%" }}>
                  <CustomInput
                    id="sso-email"
                    label="Work email"
                    placeHolder="you@company.com"
                    name="ssoEmail"
                    type="email"
                    value={ssoEmail}
                    disabled={processing}
                    autoFocus
                    onChange={(e: any) => {
                      setSsoEmail(e.target.value);
                      setSsoError("");
                    }}
                    onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSsoContinue();
                      }
                    }}
                    error={!!ssoError}
                    helperText={ssoError}
                    propsToInputElement={{ maxLength: 50 }}
                    aria-label="Work email for single sign-on"
                  />
                  <Stack direction="row" spacing={1.5}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        setShowSso(false);
                        setSsoError("");
                      }}
                      disabled={processing}
                      sx={authSecondaryButtonSx}
                    >
                      Back
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSsoContinue}
                      disabled={processing || _.isEmpty(ssoEmail)}
                      sx={authPrimaryButtonSx}
                      aria-label="Continue to your organization's single sign-on"
                    >
                      {processing ? "Redirecting..." : "Continue with SSO"}
                    </Button>
                  </Stack>
                  <Typography
                    variant="caption"
                    textAlign="center"
                    color="text.secondary"
                  >
                    You'll be redirected to your organization's sign-in page.
                  </Typography>
                </Stack>
              )}
            </Box>
          </Box>

          <Box
            component="footer"
            role="contentinfo"
            sx={{ mt: { xs: 5, md: 6 } }}
          >
            <Typography
              sx={{
                color: panelRef.metaText,
                display: "block",
                fontSize: refFont.footer,
                lineHeight: 1.6,
              }}
            >
              Protected by reCAPTCHA.{" "}
              <Box
                component="a"
                href="https://inkfree.app/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google Privacy Policy, opens in new tab"
                sx={{
                  color: panelRef.subText,
                  textDecoration: "underline",
                  "&:hover": { color: mobile.base[500] },
                }}
              >
                Privacy Policy
              </Box>
              {" · "}
              <Box
                component="a"
                href="https://inkfree.app/terms/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google Terms of Service, opens in new tab"
                sx={{
                  color: panelRef.subText,
                  textDecoration: "underline",
                  "&:hover": { color: mobile.base[500] },
                }}
              >
                Terms of Service
              </Box>
            </Typography>
            <Typography
              sx={{
                color: panelRef.metaText,
                display: "block",
                fontSize: refFont.footer,
                lineHeight: 1.6,
                mt: 0.25,
              }}
            >
              © {new Date().getFullYear()} Inkfree. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>

      {mdUp && (
        <Box
          id="login-right-panel"
          component="aside"
          aria-label="Why teams choose Inkfree"
          sx={{
            flex: "0 0 50%",
            maxWidth: "50%",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { md: 7, lg: 9 },
            backgroundColor: mobile.base[500],
          }}
        >
          {/* Ambient brand glow */}
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(120% 90% at 85% 10%, ${alpha(
                signer.purple.main,
                0.55,
              )}, transparent 60%), radial-gradient(100% 80% at 20% 100%, ${alpha(
                signer.gradient.coral,
                0.28,
              )}, transparent 55%)`,
            }}
          />

          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
              maxWidth: 560,
              width: "100%",
              mr: "auto",
            }}
          >
            <Typography
              component="h2"
              sx={{
                fontSize: { md: "3rem", lg: "3.25rem" },
                fontWeight: 800,
                color: "common.white",
                lineHeight: 1.25,
                letterSpacing: "-0.01em",
              }}
            >
              Send. Sign.
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(90deg, ${signer.gradient.amber}, ${signer.gradient.coral})`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Done.
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { md: "1.0625rem", lg: "1.125rem" },
                lineHeight: 1.65,
                mt: 2.5,
                color: alpha("#FFFFFF", 0.72),
              }}
            >
              Upload a document, add your signers, and watch signatures roll in
              — in minutes, not days.
            </Typography>

            {/* Floating document mock */}
            <Box
              aria-hidden="true"
              sx={{
                mt: 5.5,
                width: "100%",
                maxWidth: 460,
                boxSizing: "border-box",
                backgroundColor: "common.white",
                borderRadius: "12px",
                p: "24px 26px",
                boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
                display: "flex",
                flexDirection: "column",
                gap: 1.75,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "8px",
                    backgroundColor: signer.orange.tint,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: signer.orange.main,
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  >
                    <path d="M7 3h7l4 4v14H7z" />
                    <path d="M14 3v4h4" />
                  </svg>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.9375rem",
                      fontWeight: 700,
                      color: mobile.base[500],
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    Master-Services-Agreement.pdf
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      color: panelRef.metaText,
                    }}
                  >
                    2 of 3 signed
                  </Typography>
                </Box>
                <Box
                  sx={{
                    ml: "auto",
                    flexShrink: 0,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    color: signer.warn.text, // warn.main #B47207 is 3.55:1 on the tint
                    backgroundColor: panelRef.pendingTint,
                    borderRadius: "999px",
                    px: 1.25,
                    py: 0.5,
                  }}
                >
                  PENDING
                </Box>
              </Box>

              <Box
                sx={{
                  height: 6,
                  borderRadius: "999px",
                  backgroundColor: signer.border.light,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: "66%",
                    height: "100%",
                    background: `linear-gradient(90deg, ${signer.purple.main}, ${signer.gradient.coral})`,
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {[
                  { initials: "DP", bg: signer.purple.main },
                  { initials: "RM", bg: inkfree.main }, // coral #EF403A is 3.84:1 under white initials
                ].map((signerAvatar) => (
                  <Box
                    key={signerAvatar.initials}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: signerAvatar.bg,
                      color: "common.white",
                      fontSize: "0.6875rem",
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {signerAvatar.initials}
                  </Box>
                ))}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: panelRef.pendingAvatarBg,
                    border: `1px dashed ${panelRef.pendingAvatarBorder}`,
                    color: panelRef.pendingAvatarText,
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  AR
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    color: panelRef.metaText,
                    ml: 0.5,
                  }}
                >
                  Awaiting Aria's signature
                </Typography>
              </Box>
            </Box>

            {/* Proof points */}
            <Box sx={{ display: "flex", gap: 3.5, mt: 5 }}>
              {[
                { value: "606+", label: "docs completed" },
                { value: "4 min", label: "median time to sign" },
              ].map((stat, index) => (
                <Box key={stat.label} sx={{ display: "flex", gap: 3.5 }}>
                  {index > 0 && (
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: "1px",
                        backgroundColor: alpha("#FFFFFF", 0.15),
                      }}
                    />
                  )}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "1.75rem",
                        fontWeight: 800,
                        color: signer.gradient.amber,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        color: alpha("#FFFFFF", 0.6),
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Login;
